import React, { useContext, useEffect, useState } from "react";
import { 
  Box,
  Typography,
  TextField, 
  Stack, 
  Grid, 
  Button, 
  IconButton, 
  Alert,
  Checkbox,
  FormControlLabel ,
} from "@mui/material";
import { GlobalContext } from "context/Provider";
import { RowInterface } from "context/initialState/Provider";
import {
  StyledContainer,
  StyledMemberToExportContainer,
  StyledMemberListItem,
  StyledMemberToExportDivider,
  StyledHeader,
  StyledStack,
  StyledCheckBoxStack,
  HRContainer,
  StyledExportButton,
} from "./Reports.styled";
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Dayjs } from 'dayjs';
import { Includes, submitExport } from "./Reports.utility";
import Session from "helpers/session";

export default function Reports() {
  const { provider } = useContext(GlobalContext);
  const [availableList, setAvailableList] = useState<RowInterface[]>([]);
  const [exportList, setExportList] = useState<RowInterface[]>([]);
  const startDate = dayjs();
  const endDate = startDate.add(7, 'day');
  const [dateError, setDateError] = useState("");
  const [showSuccess, setSuccess] = useState("");
  const [isDisabled, setChanged] = useState(true);

  const [includes, setIncludes] = useState<Includes>({
      type: "csv",
      delivery: "download",
      notifications: false,
      notes: false,
      bgl: false,
      bp: false,
      hrt: false,
      spo2: false,
      spir: false,
      temp: false,
      wt: false,
      activity: false,
      sleep: false,
      pain: false,
      oneCSV: true,
  });

  const [startPickedDate, setStartDate] = useState<Dayjs>(startDate.subtract(30, 'day'));
  const [endPickedDate, setEndDate] = useState<Dayjs>(endDate);

  const measurementKeys: (keyof Includes)[] = [
    "bgl", "bp", "hrt", "spo2", "spir", "temp", "wt", "activity", "sleep", "pain"
  ];

  const isAllRecords = measurementKeys.every(key => includes[key] === true);

  const handleExportChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "allRecords") {
      const toggle = !isAllRecords;
      const medRecords = {
        bgl: toggle,
        bp: toggle,
        hrt: toggle,
        spo2: toggle,
        spir: toggle,
        temp: toggle,
        wt: toggle,
        activity: toggle,
        sleep: toggle,
        pain: toggle,
      };
      setIncludes((prev) => ({ ...prev, ...medRecords }));
    } else if (event.target.value === "csv") {
      setIncludes((prev) => ({
        ...prev,
        type: "csv",
        oneCSV: false,
      }));
    } else if (event.target.value === "oneCSV") {
      setIncludes((prev) => ({
        ...prev,
        type: "csv",   
        oneCSV: event.target.checked,
      }));
    } else {
      setIncludes({ ...includes, [event.target.value]: event.target.checked });
    }
    setChanged(false);
    setSuccess("");
  }

  const handleStartDateChange = (newValue: Dayjs | null) => {
    if (!newValue) return; 
    setStartDate(newValue);
    setSuccess("");
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    if (!newValue) return;
    setEndDate(newValue);
    setSuccess("");
  };

  const isDateRangeInvalid = startPickedDate.isAfter(endPickedDate);

  const handleSubmit = () => {
    if (isDateRangeInvalid) {
      setDateError("Start date is after end date");
      return;
    }
    setDateError("");

    const formattedStart = startPickedDate.format("YYYY-MM-DD");
    const formattedEnd = endPickedDate.format("YYYY-MM-DD");
    const startDate = new Date(formattedStart);
    const endDate = new Date(formattedEnd);
    const sessionAuthorization = Session.crackCookieToken("coordinator");
    const selectedMemberIDs = exportList.map((member) => member.id);

    if (includes.delivery === "download") {
      submitExport(
        {
          ...includes,
        },
        startDate,
        endDate,
        sessionAuthorization,
        selectedMemberIDs || ""
      );
      setSuccess("success");
      setChanged(true);
    }
  }

  const handleRemoveAll = () => {
    setAvailableList((currentAvailableList) => [
      ...currentAvailableList,
      ...exportList.filter(
        (member) => !currentAvailableList.some((memberInAvailable) => memberInAvailable.id === member.id),
      ),
    ]);
    setExportList([]);
  };

  const handleAddAll = () => {
    setExportList((currentExportList) => [
      ...currentExportList,
      ...availableList.filter(
        (member) => !currentExportList.some((memberInExport) => memberInExport.id === member.id),
      ),
    ]);
    setAvailableList([]);
  };

  const handleToggleMember = (member: RowInterface, isInExportList: boolean) => {
    setAvailableList((previousAvailableList) => {
      const updatedAvailableList = isInExportList
        ? [...previousAvailableList, member].filter(
          (currentMember, index, fullMemberList) =>
            currentMember.id !== member.id || index === fullMemberList.findIndex((memberToCompare) => memberToCompare.id === currentMember.id)
        )
        : previousAvailableList.filter((currentMember) => currentMember.id !== member.id);
      return updatedAvailableList;
    });

    setExportList((previousExportList) => {
      const newExportList = isInExportList
        ? previousExportList.filter((currentMember) => currentMember.id !== member.id)
        : [...previousExportList, member].filter(
          (currentMember, index, fullMemberList) =>
            currentMember.id !== member.id || index === fullMemberList.findIndex((memberToCompare) => memberToCompare.id === currentMember.id)
        );
      return newExportList;
    });
  };

  useEffect(() => {
    if (provider?.memberList) {
      setAvailableList(provider.memberList);
    }
  }, [provider]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'column', md: 'row' },
        width: '100%',
        height: '98%',
        gap: 1,
        boxSizing: 'border-box',
      }}
    >
      <StyledContainer>
        <Grid container>
          <Grid item sm={11.2} md={10.4} lg={11.3}>
            <StyledHeader>
              <h2>
                Export Details
              </h2>
            </StyledHeader>
            <StyledStack spacing={1}>
              <HRContainer text={"Selection"}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems={"center"}
                    justifyContent={"center"}
                  >
                    <MobileDatePicker
                      label={"Start Date"}
                      inputFormat="MM/DD/YYYY"
                      value={startPickedDate}
                      onChange={handleStartDateChange}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          sx={{ 
                            "& .MuiOutlinedInput-root": { borderRadius: 0 }
                          }} 
                        />
                      )}
                    />
                    <MobileDatePicker
                      label={"End Date"}
                      inputFormat="MM/DD/YYYY"
                      value={endPickedDate}
                      onChange={handleEndDateChange}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          sx={{ 
                            "& .MuiOutlinedInput-root": { borderRadius: 0 }
                          }} 
                        />
                      )}
                    />
                    <FormControlLabel
                      value={"allRecords"}
                      label={"Select All Measurements"}
                      control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={isAllRecords}
                      />
                      }
                    />
                  </Stack>
                  {dateError && (
                    <Alert severity="error" sx={{ mt: 1 }} >
                      {dateError}
                    </Alert>
                  )}
                </LocalizationProvider>
              </HRContainer>
              <HRContainer text={`Notes and Notifications`}>
                <StyledCheckBoxStack direction={"row"}>
                  <FormControlLabel
                    label={"Notes"}
                    value={"notes"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.notes}
                      />
                    }
                  />
                  <FormControlLabel
                    label={"Notifications"}
                    value={"notifications"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.notifications}
                      />
                    }
                  />
                </StyledCheckBoxStack>
              </HRContainer>
              <HRContainer text={"Measurements"}>
                <div>
                  <FormControlLabel
                    label={"Blood Glucose"}
                    value={"bgl"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.bgl}
                      />
                    }
                  />
                  <FormControlLabel
                    label={"Blood Pressure"}
                    value={"bp"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.bp}
                      />
                    }
                  />
                  <FormControlLabel
                    label={"Heart Rate"}
                    value={"hrt"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.hrt}
                      />
                    }
                  />
                  <FormControlLabel
                    label={"Oxygen Saturation"}
                    value={"spo2"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.spo2}
                      />
                    }
                  />
                  <FormControlLabel
                    label={"Spirometry"}
                    value={"spir"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.spir}
                      />
                    }
                  />
                  <FormControlLabel
                    label={"Temperatures"}
                    value={"temp"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.temp}
                      />
                    }
                  />
                  <FormControlLabel
                    label={"Weight"}
                    value={"wt"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.wt}
                      />
                    }
                  />
                  <FormControlLabel
                    label={"Activity"}
                    value={"activity"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.activity}
                      />
                    }
                  />
                  <FormControlLabel
                    label={"Sleep"}
                    value={"sleep"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.sleep}
                      />
                    }
                  />
                  <FormControlLabel
                    label={"Pain"}
                    value={"pain"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.pain}
                      />
                    }
                  />
                </div>
              </HRContainer>
              <HRContainer text={"Format"}>
                <StyledCheckBoxStack direction={"row"}>
                  <FormControlLabel
                    value="csv"
                    label={"Separate CSV Files"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.type === "csv" && !includes.oneCSV}
                      />
                    }
                  />
                  <FormControlLabel
                    value="oneCSV"
                    label={"Single CSV File"}
                    control={
                      <Checkbox
                        onChange={handleExportChange}
                        checked={includes.oneCSV}
                      />
                    }
                  />
                </StyledCheckBoxStack>
              </HRContainer>
            </StyledStack>
          </Grid>
        </Grid>
      </StyledContainer>

      <StyledMemberToExportContainer>
        <Grid container alignItems="flex-end" justifyContent="flex-end" sx={{ mb: 3 }}>
          <Grid item>
            <Stack direction="column" spacing={2} alignItems="flex-end">
              <StyledExportButton 
                variant="contained"
                type="submit"
                disabled={isDisabled || isDateRangeInvalid}
                onClick={handleSubmit}
              >
                Download Export
              </StyledExportButton>
              {showSuccess === "success" ? (
                <Alert severity="success" >
                  Your Report is being generated.
                </Alert>
              ) : (
                ""
              )}
              <Stack direction="row" spacing={4}>
                <Button
                  onClick={handleAddAll}
                  variant="text"
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '1.1rem', 
                    padding: 0,
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  Add All
                </Button>

                <Button
                  onClick={handleRemoveAll}
                  variant="text"
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '1.1rem', 
                    paddingRight: 2,
                    color: 'primary.main',
                    '&:hover': {
                      textDecoration: 'underline',
                      backgroundColor: 'transparent',
                    }, 
                  }}
                >
                  Remove All
                </Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Typography variant="h6" gutterBottom >
              Members to Export
            </Typography>
            <StyledMemberToExportDivider>
              {exportList.map((member) => (
                <StyledMemberListItem key={member.id} >
                  <Typography>
                    {member.lastName}, {member.firstName}
                  </Typography>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleToggleMember(member, true)}
                  >
                    <RemoveIcon />
                  </IconButton>
                </StyledMemberListItem>
              ))}
            </StyledMemberToExportDivider>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6" gutterBottom >
              Available Members
            </Typography>
            <StyledMemberToExportDivider>
              {availableList.map((member) => (
                <StyledMemberListItem key={member.id} >
                  <Typography>
                    {member.lastName}, {member.firstName}
                  </Typography>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleToggleMember(member, false)}
                  >
                    <AddIcon />
                  </IconButton>
                </StyledMemberListItem>
              ))}
            </StyledMemberToExportDivider>
          </Grid>
        </Grid>
      </StyledMemberToExportContainer>
    </Box>
  );
}